import type { CollectionRef } from './collections';
import type { PersonRef } from './people';

export type ListingStatus = 'active' | 'sold' | 'withdrawn';
export type PublishState = 'draft' | 'scheduled' | 'published';
export type ListingType = 'sale' | 'job' | 'event' | 'service' | 'housing' | 'other';

export type Photo = { id: string; url: string; position: number; width: number; height: number };

/** No contact details: people reach a seller through messages, not an email. */
export type Listing = {
  id: string;
  collection: CollectionRef;
  author: PersonRef;
  status: ListingStatus;
  /** Author only. */
  publishState: PublishState | null;
  /** Author only. */
  publishAt: string | null;
  announcedAt: string | null;
  title: string;
  description: string;
  listingType: ListingType;
  /** Decimal string, e.g. "150.00". */
  price: string | null;
  location: string;
  expiresAt: string | null;
  isExpired: boolean;
  /** Sorted by `position`. */
  photos: Photo[];
  /** 0 while comments are off. */
  commentCount: number;
  createdAt: string;
  updatedAt: string;
};

export type ListingRef = { id: string; title: string };

/** A photo uploaded before its listing exists; its id goes in the create body. */
export type PendingPhoto = {
  id: string;
  url: string;
  originalFilename: string;
  width: number | null;
  height: number | null;
  expiresAt: string | null;
};

export type PublishChoice = 'now' | 'draft' | 'scheduled';

/** My own listings as Drafts and My Listings split them (021 FR-011b). */
export type MyListingsShelf = 'draft' | 'notDraft';

/** Only a draft may be untitled; publishing or scheduling one needs a title (021 FR-010). */
export const ADD_A_TITLE = 'Add a title before publishing.';
export const KEEP_A_TITLE = 'A published listing needs a title.';

export type NewListing = {
  /** Null: my default collection. */
  collectionId: string | null;
  title: string;
  description: string;
  listingType: ListingType;
  price: string | null;
  location: string;
  expiresAt: string | null;
  publish: PublishChoice;
  publishAt: string | null;
  pendingPhotoIds: string[];
};

export type ListingChanges = {
  title?: string;
  description?: string;
  listingType?: ListingType;
  price?: string | null;
  location?: string;
  expiresAt?: string | null;
};

export type ListingStatusAction =
  'publishNow' | 'schedule' | 'unpublish' | 'markSold' | 'markForSale' | 'withdraw' | 'republish';

export type ManifestEntry = { id: string; collectionId: string; updatedAt: string };
